import { Maximize2 } from "lucide-react";
import { type CSSProperties, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router";

import { MultiFilterDropdown, type MultiFilterOption } from "nazare-ui/components";
import { PageSectionAction } from "nazare-ui/components/page-section";

import { LoadingBar } from "../../../loading-bar";
import { SkeletonRow } from "../../../skeleton-row";
import { TableSection } from "../../../table-section";
import { useWorkflowDefinitions, useWorkflowRuns } from "../../../workflows-api";
import { TriggerChip } from "./pages/run-detail-status-chip";
import { runElapsedMs, formatDuration } from "./pages/run-detail-format";
import { useNow } from "./pages/use-now";
import { RUNS_COLUMN_WIDTHS } from "./runs-columns";
import styles from "./runs-table.module.css";
import { StatusChip } from "./pages/run-detail-status-chip";
import { workflowRunPauseState } from "../../../workflow-run-control";
import { workflowDisplayLabel } from "./display-labels";
import { useWorkflowQueryState } from "../../../workflow-query-state";

export type WorkflowFilterOption = MultiFilterOption<string>;

export function WorkflowFilterDropdown({
  options,
  values,
  onChange,
  label,
  emptyLabel,
}: {
  options: WorkflowFilterOption[];
  values: string[];
  onChange: (values: string[]) => void;
  label: string;
  emptyLabel: string;
}) {
  const active =
    values.length === 0
      ? emptyLabel
      : values.length === 1
        ? options.find((option) => option.value === values[0])?.label ?? label
        : `${values.length.toLocaleString("en-GB")} selected`;

  return (
    <MultiFilterDropdown
      options={options}
      selectedValues={values}
      onSelectedValuesChange={onChange}
      label={label}
      triggerLabel={active}
    />
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

function duration(run: Parameters<typeof runElapsedMs>[0], now: number): string {
  const milliseconds = runElapsedMs(run, now);
  return milliseconds === null ? "—" : formatDuration(milliseconds);
}

export function WorkflowRunsTable() {
  const now = useNow();
  const location = useLocation();
  const { filters } = useWorkflowQueryState();
  const { workflow, status, period, from, to } = filters;
  const definitions = useWorkflowDefinitions();
  const runsQuery = useWorkflowRuns({ workflowName: workflow, status, period, from, to, limit: 50 });
  const runs = runsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const emptyPageFetches = useRef(0);
  const filterKey = `${workflow.join("\u0000")}\u0001${status.join("\u0000")}\u0001${period}\u0001${from ?? ""}\u0001${to ?? ""}`;
  useEffect(() => {
    emptyPageFetches.current = 0;
  }, [filterKey]);
  useEffect(() => {
    if (runs.length > 0) {
      emptyPageFetches.current = 0;
      return;
    }
    if (runsQuery.hasNextPage && !runsQuery.isFetchingNextPage && emptyPageFetches.current < 4) {
      emptyPageFetches.current += 1;
      void runsQuery.fetchNextPage();
    }
  }, [runs.length, runsQuery.fetchNextPage, runsQuery.hasNextPage, runsQuery.isFetchingNextPage]);
  return (
    <TableSection
      style={{ viewTransitionName: "wf-card-runs" } as CSSProperties}
      title="Runs"
      count={!runsQuery.error && runsQuery.data && !runsQuery.isPlaceholderData ? runs.length : undefined}
      description={!runsQuery.error && !runsQuery.isPlaceholderData && runsQuery.hasNextPage ? "Loaded runs; more available" : undefined}
      overlay={runsQuery.isFetching && !runsQuery.isLoading && <LoadingBar />}
      actions={<>
          <PageSectionAction asChild aria-label="Expand Runs" title="Expand Runs">
            <Link to={`/workflows/runs${location.search}`} viewTransition><Maximize2 aria-hidden="true" /></Link>
          </PageSectionAction>
      </>}
    >
      <table className={styles.table}>
        <colgroup>
          {RUNS_COLUMN_WIDTHS.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <thead><tr><th className={styles.th}>Status</th><th className={styles.th}>Workflow</th><th className={styles.th}>Run ID</th><th className={styles.th}>Created</th><th className={styles.th}>Artefacts</th><th className={`${styles.th} ${styles.thRight}`}>Duration</th></tr></thead>
        <tbody>
          {runsQuery.isLoading ? Array.from({ length: 6 }, (_, index) => <SkeletonRow key={index} columns={6} />) : runsQuery.error ? (
            <tr><td className={styles.td} colSpan={6}>Couldn’t load workflow runs.</td></tr>
          ) : runs.length === 0 && (runsQuery.isFetchingNextPage || (runsQuery.hasNextPage && emptyPageFetches.current < 4)) ? (
            <tr><td className={styles.td} colSpan={6}>Scanning workflow runs…</td></tr>
          ) : runs.length === 0 && runsQuery.hasNextPage ? (
            <tr><td className={styles.td} colSpan={6}>No matching runs found in the first four pages. <button type="button" className={styles.emptyResultAction} onClick={() => { emptyPageFetches.current = 0; void runsQuery.fetchNextPage(); }}>Load more runs</button></td></tr>
          ) : runs.length === 0 ? (
            <tr><td className={styles.td} colSpan={6}>{runsQuery.hasNextPage ? "No matches in the loaded page. Expand Runs to load more." : "No workflow runs match these filters."}</td></tr>
          ) : runs.map((run) => (
            <tr key={run.runId} className={styles.row}>
              <td className={styles.td}>
                <Link className={styles.rowLink} to={`/workflows/runs/${encodeURIComponent(run.runId)}`} aria-label={`View run ${run.runId}`} />
                <span className={styles.statusCell}><StatusChip status={run.status} pauseState={workflowRunPauseState(run)} /></span>
              </td>
              <td className={styles.td}>
                <span className={styles.workflowCell}>
                  <span className={styles.workflowName}>{workflowDisplayLabel(run, definitions.data?.definitions)}</span>
                  {run.label && <span className={styles.workflowSecondary} title={run.label}>{run.label}</span>}
                  <TriggerChip trigger={run.triggeredBy} />
                </span>
              </td>
              <td className={`${styles.td} ${styles.runId}`} title={run.runId}>{run.runId}</td>
              <td className={styles.td}>{formatDate(run.createdAt)}</td>
              <td className={styles.td}>{typeof run.artifactCount === "number" ? run.artifactCount.toLocaleString("en-GB") : "—"}</td>
              <td className={`${styles.td} ${styles.duration}`}>{duration(run, now)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableSection>
  );
}
